import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { billingApi, BillingPlanInfo } from '../services/api';
import { OrganizationPlan } from '@dsgvo-buddy/types';
import {
  Title,
  Text,
  Button,
  Card,
  CardHeader,
  FlexBox,
  MessageStrip,
  BusyIndicator,
  Icon,
  ObjectStatus,
  Table,
  TableColumn,
  TableRow,
  TableCell
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';

export default function BillingPage() {
  const [searchParams] = useSearchParams();
  const [planInfo, setPlanInfo] = useState<BillingPlanInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState('');

  const success = searchParams.get('success') === 'true';
  const canceled = searchParams.get('canceled') === 'true';

  useEffect(() => {
    loadPlan();
  }, []);

  const loadPlan = async () => {
    setLoading(true);
    const response = await billingApi.getPlan();
    if (response.success && response.data) {
      setPlanInfo(response.data);
    }
    setLoading(false);
  };

  const handleUpgrade = async () => {
    setUpgrading(true);
    setError('');
    const response = await billingApi.createCheckout();
    if (response.success && response.data?.checkout_url) {
      window.location.href = response.data.checkout_url;
    } else {
      setError(response.error || 'Fehler beim Erstellen der Checkout-Session');
      setUpgrading(false);
    }
  };

  const handleManageSubscription = async () => {
    const response = await billingApi.createPortalSession();
    if (response.success && response.data?.portal_url) {
      window.location.href = response.data.portal_url;
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <BusyIndicator active size="Large" />
      </div>
    );
  }

  const currentPlan = planInfo?.plan || OrganizationPlan.STARTER;
  const isProfessional = currentPlan === OrganizationPlan.PROFESSIONAL || currentPlan === OrganizationPlan.ENTERPRISE;
  // Display price for comparison table
  const priceLabel = 'Professional (7,99 EUR pro Monat netto)';

  return (
    <div className="page-layout--form">
      <Title level="H3" style={{ marginBottom: '1.5rem' }}>Abonnement & Abrechnung</Title>

      {success && (
        <MessageStrip design="Positive" hideCloseButton style={{ marginBottom: '1rem' }}>
          Upgrade erfolgreich! Ihr Plan wurde auf Professional aktualisiert. Alle Funktionen sind jetzt freigeschaltet.
        </MessageStrip>
      )}
      {canceled && (
        <MessageStrip design="Warning" hideCloseButton style={{ marginBottom: '1rem' }}>
          Upgrade abgebrochen. Ihr Plan wurde nicht geändert.
        </MessageStrip>
      )}
      {error && (
        <MessageStrip design="Negative" hideCloseButton style={{ marginBottom: '1rem' }}>
          {error}
        </MessageStrip>
      )}

      {/* Aktueller Plan */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <CardHeader titleText="Ihr aktueller Plan" />
        <div style={{ padding: '1rem 1.5rem 1.5rem' }}>
          <FlexBox justifyContent="SpaceBetween" alignItems="Center" wrap="Wrap" style={{ gap: '1.5rem' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <Title level="H4">{isProfessional ? 'Professional' : 'Starter'}</Title>
                <ObjectStatus state={isProfessional ? 'Success' : 'Information'} showDefaultIcon>
                  {isProfessional ? 'Aktiv' : 'Kostenlos'}
                </ObjectStatus>
              </div>
              <Text style={{ color: 'var(--sapNeutralTextColor)' }}>
                {isProfessional
                  ? '7,99 EUR pro Monat -- Alle Funktionen unbegrenzt freigeschaltet'
                  : 'Kostenloser Plan (max. 10 Einträge pro Bereich) -- Jetzt upgraden für unbegrenzte Nutzung'}
              </Text>
              {planInfo && (
                <Text style={{ display: 'block', marginTop: '0.5rem', color: 'var(--sapNeutralTextColor)', fontSize: '0.8125rem' }}>
                  Organisation: {planInfo.organization_name} -- {planInfo.user_count} Benutzer
                </Text>
              )}
            </div>
            <div>
              {isProfessional ? (
                <Button design="Default" onClick={handleManageSubscription} icon="action-settings">
                  Abonnement verwalten
                </Button>
              ) : (
                <Button design="Emphasized" onClick={handleUpgrade} disabled={upgrading} icon="cart">
                  {upgrading ? 'Weiterleitung...' : 'Auf Professional upgraden'}
                </Button>
              )}
            </div>
          </FlexBox>
        </div>
      </Card>

      {/* Plan-Vergleich */}
      <Card>
        <CardHeader titleText="Funktionsvergleich" />
        <Table
          columns={
            <>
              <TableColumn><Text>Funktion</Text></TableColumn>
              <TableColumn><Text>Starter (kostenlos)</Text></TableColumn>
              <TableColumn><Text>{priceLabel}</Text></TableColumn>
            </>
          }
        >
          {planComparison.map(row => (
            <TableRow key={row.feature}>
              <TableCell><Text style={{ fontWeight: row.highlight ? 600 : 400 }}>{row.feature}</Text></TableCell>
              <TableCell>
                {typeof row.starter === 'boolean' ? (
                  <Icon
                    name={row.starter ? 'accept' : 'decline'}
                    style={{ color: row.starter ? 'var(--sapPositiveColor)' : 'var(--sapNegativeColor)' }}
                  />
                ) : (
                  <Text>{row.starter}</Text>
                )}
              </TableCell>
              <TableCell>
                {typeof row.professional === 'boolean' ? (
                  <Icon
                    name={row.professional ? 'accept' : 'decline'}
                    style={{ color: row.professional ? 'var(--sapPositiveColor)' : 'var(--sapNegativeColor)' }}
                  />
                ) : (
                  <Text style={{ fontWeight: 600 }}>{row.professional}</Text>
                )}
              </TableCell>
            </TableRow>
          ))}
        </Table>

        {!isProfessional && (
          <div style={{ padding: '1.5rem', textAlign: 'center' }}>
            <Button design="Emphasized" onClick={handleUpgrade} disabled={upgrading} icon="cart">
              {upgrading ? 'Weiterleitung...' : 'Jetzt auf Professional upgraden'}
            </Button>
          </div>
        )}
      </Card>

      {!planInfo?.stripe_configured && !isProfessional && (
        <MessageStrip design="Information" style={{ marginTop: '1rem' }}>
          Stripe ist noch nicht konfiguriert. Bitte setzen Sie STRIPE_SECRET_KEY und STRIPE_PRICE_ID in der Backend .env-Datei, um Zahlungen zu aktivieren.
        </MessageStrip>
      )}
    </div>
  );
}

const planComparison = [
  { feature: 'Verarbeitungstätigkeiten (VVT)', starter: 'Max. 10', professional: 'Unbegrenzt', highlight: true },
  { feature: 'Dienstleister & AVV', starter: 'Max. 10', professional: 'Unbegrenzt', highlight: true },
  { feature: 'TOM-Dokumentation', starter: 'Max. 10', professional: 'Unbegrenzt', highlight: true },
  { feature: 'Datenschutzhinweise', starter: 'Max. 10', professional: 'Unbegrenzt', highlight: true },
  { feature: 'Betroffenenrechte', starter: 'Max. 10', professional: 'Unbegrenzt', highlight: true },
  { feature: 'Datenschutzvorfälle', starter: 'Max. 10', professional: 'Unbegrenzt', highlight: true },
  { feature: 'Benutzer', starter: '1', professional: 'Bis zu 25', highlight: true },
  { feature: 'PDF-Export', starter: true, professional: true, highlight: false },
  { feature: 'Freigabe-Workflows', starter: true, professional: true, highlight: false },
  { feature: 'Audit-Protokoll', starter: true, professional: true, highlight: false },
  { feature: 'JSON-Import', starter: true, professional: true, highlight: false },
];
